<template>
  <ion-page>
    <toolbar :title="t(`${organisationPrefix}hzba.bestandsaufnahmen`)">
      <template #trailing>
        <UserDialogButton />
      </template>
    </toolbar>
    <ion-content>
      <ion-refresher
        slot="fixed"
        @ionRefresh="doRefresh($event)"
      >
        <ion-refresher-content />
      </ion-refresher>

      <div
        style="background: white"
        class="pt-8"
      >
        <div class="container pb-4 px-4 lg:px-4">
          <div class="mt-8 mb-4 flex align-center">
            <h1 class="flex-1 text-2xl md:text-3xl lg:mx-auto">
              {{ t(`${organisationPrefix}hzba.bestandsaufnahmen`) }}
            </h1>
            <div class="flex flex-col items-end">
              <div class="mb-4 flex flex-row items-center">
                <div
                  v-if="canCreateSurvey && featureFlags?.createSurvey"
                  class="createSurveyButtonHolder"
                >
                  <ion-icon
                    v-if="!isSelectModeEnabled"
                    slot="icon-only"
                    class="h-14 w-14 cursor-pointer create-survey-button"
                    :icon="addOutline"
                    @click="openCreateSurveyModal"
                  />
                </div>
                <div
                  v-if="featureFlags?.survey?.exportxls || featureFlags?.survey?.exportgeojson"
                  class="ml-3"
                >
                  <ExportButtons
                    :show-as-start-button="isSelectModeEnabled"
                    @export-geojson="exportGeoJson"
                  />
                </div>
              </div>
              <!-- <div
                v-if="featureFlags?.downloadSurvey"
                class="popoverButtonHolder mb-4"
              >
                <DownloadSurveysPopoverButton
                  @downloadAll="downloadAll"
                  @downloadPlannedForToday="downloadPlannedForToday"
                  @downloadFiltered="downloadFiltered"
                />
              </div> -->
              <div class="flex">
                <ion-label
                  v-if="displayCountLabel"
                  class="text-lg text-right"
                >
                  {{ displayCountLabel }}
                </ion-label>
                <ion-spinner
                  v-if="totalCount === null"
                  class="ml-2 h-5 w-5"
                  color="light"
                />
              </div>
            </div>
          </div>

          <Searchbar
            v-model:search-term="searchTerm"
            v-model:sort-term="sortTerm"
          />
          <Filters
            v-model:selected-filters="selectedFilters"
            :filter-options="filterOptions"
            @update:date-filter="dateFilter = $event"
          />
        </div>
      </div>
      <div class="container pb-4 px-4 lg:px-4">
        <div class="py-4 block md:flex flex-row-reverse text-xl items-center">
          <div class="flex-1 md:text-right">
            <sort-by
              v-model:sort-term="sortTerm"
              :sort-terms="supportedSortTerms"
            />
          </div>
          <div>
            <div>{{ t("hzba.zuletztAktualisiertAm") }}: {{ lastFetch }}
              <a-button @click="() => doRefresh($event)">
                Refresh
              </a-button>
            </div>
            <div
              v-if="isSelectModeEnabled"
              class="mt-4 justify-self-start flex flex-col sm:flex-row flex-wrap items-start sm:items-center"
            >
              <ion-label class="mb-2 sm:mb-0 pr-8">
                {{ selectModeState === SelectModeState.ALL ? totalCount : countOfSelectedCards }} selektiert
              </ion-label>
              <div class="button-group flex">
                <ion-button
                  class="action-button group-button first"
                  @click="selectAllCards"
                >
                  {{ $t('hzba.export.all') }} ({{ totalCount }})
                </ion-button>
                <ion-button
                  class="action-button group-button middle"
                  @click="deselectAllCards"
                >
                  {{ $t('hzba.export.none') }}
                </ion-button>
                <ion-button
                  class="action-button group-button last"
                  @click="stopSelectMode"
                >
                  {{ $t('hzba.export.abortExport') }}
                </ion-button>
              </div>
            </div>
          </div>
        </div>

        <offline-hint
          class="mb-4"
          style="margin-top: 0;"
        >
          {{ t(`${organisationPrefix}hzba.noInternetConnectionInfo`) }}
        </offline-hint>

        <socket-disconnected-hint class="mb-4">
          {{ t(`${organisationPrefix}hzba.noSocketConnectionInfo`) }}
        </socket-disconnected-hint>

        <hzba-list
          v-if="loaded"
          :hzbas="bas"
          :enable-select="isSelectModeEnabled"
        />
        <Skeleton v-else />

        <div
          v-if="showLoadMore"
          class="flex flex-row items-center justify-center"
        >
          <AButton
            type="submit"
            :btn-primary="true"
            class="px-16 py-6 mb-2 text-lg"
            data-cy="button-login"
            style="margin-top: 32px !important; margin-left: 0"
            @click="loadMore()"
          >
            {{ t("buttons.loadMore") }}
          </AButton>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import Toolbar from "@/components/Navigation/Toolbar";
import HzbaList from "@/components/hzba/HzbaList.vue";
import DownloadSurveysPopoverButton from "@/components/v2/General/DownloadSurveysPopover.vue";
import { useStore } from "@/composables/useTypedStore";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonIcon,
  IonImg,
  IonLabel,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSpinner,
  onIonViewWillEnter
} from "@ionic/vue";
import { ComputedRef, computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import AButton from "@/components/Base/AButton";
import Filters from "@/components/Base/Filters.vue";
import SortBy from "@/components/Base/SortBy.vue";
import OfflineHint from "@/components/Other/OfflineHint.vue";
import SocketDisconnectedHint from "@/components/Other/SocketDisconnectedHint.vue";
import UserDialogButton from "@/components/User/UserDialogButton.vue";
import Searchbar from "@/components/hzba/Base/Searchbar.vue";
import ExportButtons from "@/components/v2/General/ExportButtons.vue";
import { FilterOption } from "@/composables/Bestandsaufnahme/useBestandsaufnahmens";
import { SortTerm, SortTermActive } from "@/composables/Sort/SortTerms";
import useAlert from "@/composables/useAlert";
import useFetchData from "@/composables/useFetchData";
import useUser from "@/composables/useUser";
import { openCreateSurveyModal } from '@/modals/modal-helper';
import { HzbaStatusCode, translatedHzbaStatus } from "@/models/ba/interfaces/IBestandsaufnahme";
import BestandsaufnahmeModel from "@/models/ba/models/bestandsaufnahme.model";
import { getLocalMetaData } from "@/models/local-meta-data";
import User from "@/models/user";
import { DateFilters } from "@/store/store.types";
import { SelectModeState } from "@/types/app/survey-types";
import { compareArraysById } from "@/utilities/compare-helper";
import { getPhotoUrl } from "@/utilities/get-media-url";
import { popoverHelper } from "@/utilities/popover-helper";
import { addOutline, swapVerticalOutline } from "ionicons/icons";
import moment from "moment";
import Skeleton from "../components/Skeleton.vue";

export default {
  name: "HzbasPage",
  components: {
    SortBy,
    DownloadSurveysPopoverButton,
    Filters,
    OfflineHint,
    SocketDisconnectedHint,
    Searchbar,
    Skeleton,
    Toolbar,
    IonPage,
    IonContent,
    HzbaList,
    IonButton,
    IonButtons,
    IonImg,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    UserDialogButton,
    AButton,
    IonSpinner,
    IonLabel,
    ExportButtons,
    IonIcon
  },
  setup() {
    const { t } = useI18n({ useScope: "global" });
    const store = useStore();
    const { user, canCreateSurvey } = useUser();
    const useData = useFetchData();
    const alert = useAlert();
    const appConfig = computed(() => store.state.app.appConfig);
    const featureFlags = computed(() => user.value?.organisation?.featureFlags);
    const organisationPrefix: ComputedRef<string> = computed(() => {
      const currentUser: User | null = User.query().first();

      return currentUser ? currentUser.organisationPrefix : "";
    });

    const countOfSelectedCards = computed(() => {
      return store.state.entities.bestandsaufnahmes.countOfSelectedSurveyCards;
    });
    const selectModeState = computed(() => {
      return store.state.entities.bestandsaufnahmes.selectModeSurveyCards;
    });

    const searchTerm = ref<string | undefined>("");
    const isSelectModeEnabled = ref(false);

    BestandsaufnahmeModel.dispatch("setSortBy", { label: t(`${organisationPrefix.value}hzba.datum.begehung`), fieldName: 'begehungsdatum', orderBy: 'desc' });
    const sortTerm = ref<SortTermActive | undefined>(BestandsaufnahmeModel.getters("sortBy"));

    /**
     * Options to filter bestandsaufnahmes
     * filterOptions were moved here from useBestandsaufnahmens.ts due to I18n not useable outside setup
     */
    const filterOptions: FilterOption[] = [
      { name: translatedHzbaStatus(HzbaStatusCode.OFFLINE, t), id: HzbaStatusCode.OFFLINE },
      { name: translatedHzbaStatus(HzbaStatusCode.ANGELEGT, t), id: HzbaStatusCode.ANGELEGT },
      { name: translatedHzbaStatus(HzbaStatusCode.GEPLANT, t), id: HzbaStatusCode.GEPLANT },
      { name: translatedHzbaStatus(HzbaStatusCode.IN_DURCHFUEHRUNG, t), id: HzbaStatusCode.IN_DURCHFUEHRUNG },
      { name: translatedHzbaStatus(HzbaStatusCode.ABGESCHLOSSEN, t), id: HzbaStatusCode.ABGESCHLOSSEN },
      { name: translatedHzbaStatus(HzbaStatusCode.FREIGEGEBEN, t), id: HzbaStatusCode.FREIGEGEBEN },
      { name: translatedHzbaStatus(HzbaStatusCode.ARCHIVIERT, t), id: HzbaStatusCode.ARCHIVIERT },
      { name: translatedHzbaStatus(HzbaStatusCode.RESET, t), id: HzbaStatusCode.RESET, resetOtherFilters: true, resetSelfWhenOthersActive: true, onlyShowWhenOthersActive: true }

    ];

    /**
     * Options to sort.
     */
    const supportedSortTerms: SortTerm[] = [
      { label: t(`${organisationPrefix.value}hzba.datum.begehung`), fieldName: 'begehungsdatum' },
      { label: t('hzba.vorschau.sortLabels.sortStatus'), fieldName: 'status' },
      { label: t('hzba.vorschau.sortLabels.sortLeitung'), fieldName: 'stadt', subObject: 'immobilie' },
      { label: t('hzba.vorschau.sortLabels.sortMast'), fieldName: 'name', subObject: 'immobilie' },
      { label: t('hzba.vorschau.sortLabels.sortObjekt'), fieldName: 'externeObjektNr', subObject: 'immobilie' },
      { label: 'Bewertung', fieldName: 'malusColor' }
    ];

    onIonViewWillEnter(
      () => {
        store.commit("app/showBottomNavigation");
        // resetSearch();
      }
    );

    const bestandsaufnahmes = computed(() => BestandsaufnahmeModel.getters("moverSurveys"));
    const bas = computed(() => BestandsaufnahmeModel.getters("moverSurveysJson"));
    const loaded = computed(() => BestandsaufnahmeModel.getters("loaded"));

    const lastFetch = computed(() => {
      const date = moment(getLocalMetaData("lastBackendFetch"));
      return `${date.format("DD.MM.YYYY")} / ${date.format("HH:mm:ss")} Uhr`;
    });

    const isNetworkConnected = computed(() => store.state.app.networkConnected);

    const retrievedCount = computed(() => store.state.entities.bestandsaufnahmes.searchResultsCount);
    const totalCount = computed(() => store.state.entities.bestandsaufnahmes.totalSearchResultsCount);

    const displayCountLabel = computed(() => {
      if (totalCount.value === 0) {
        return t("hzba.searchResults.noResults");
      } else if (totalCount.value === 1) {
        return t("hzba.searchResults.resultsCount.one");
      } else if (retrievedCount.value === null || totalCount.value === null) {
        return t("hzba.searchResults.loadingLabel");
      } else if (BestandsaufnahmeModel.getters("page") >= BestandsaufnahmeModel.getters("pageCount")) {
        return t("hzba.searchResults.resultsCount.other", { count: totalCount.value });
      } else {
        return t("hzba.searchResults.resultsCountOf", { retrieved: retrievedCount.value, total: totalCount.value });
      }
    });

    const selectedFilters = ref([]);
    const dateFilter = ref<DateFilters>([]);

    const exportGeoJson = () => {
      isSelectModeEnabled.value = true;
    };

    const selectAllCards = () => {
      BestandsaufnahmeModel.dispatch("selectAllSurveyCards");
    };

    const deselectAllCards = () => {
      BestandsaufnahmeModel.dispatch("deselectAllSurveyCards");
    };

    const stopSelectMode = () => {
      deselectAllCards();
      isSelectModeEnabled.value = false;
    };

    const resetSearch = () => {
      BestandsaufnahmeModel.commit(state => state.page = 0);
      BestandsaufnahmeModel.deleteAll();
      BestandsaufnahmeModel.dispatch("loadBestandsaufnahmens");
      deselectAllCards();
    };

    watch(searchTerm, (value) => {
      BestandsaufnahmeModel.dispatch("setSearchTerm", value);
      resetSearch();
    });

    watch(sortTerm, (value) => {
      BestandsaufnahmeModel.dispatch("setSortBy", value);
      resetSearch();
    });

    watch(selectedFilters, (newFilters, oldFilters) => {
      BestandsaufnahmeModel.dispatch("setStateFilter", newFilters);
      if (!compareArraysById(
        newFilters.filter((filter: FilterOption) => !filter.id.startsWith("LOCALE_")),
        oldFilters.filter((filter: FilterOption) => !filter.id.startsWith("LOCALE_"))
      )) {
        resetSearch();
      }
    });

    watch(dateFilter, (value) => {
      BestandsaufnahmeModel.dispatch("setDateFilter", value);
      resetSearch();
    });

    watch(isNetworkConnected, (connected, oldConnected) => {
      // If /bas is the first page loaded, this watcher would cause a reset of the page.
      // For this reason, check if oldConnected is "false" or undefined before resetting the search.
      if (oldConnected === "false" && connected) {
        resetSearch();
      }
    });

    watch(bestandsaufnahmes, (results: BestandsaufnahmeModel[]) => {
      const isOffline = store.getters["app/isOffline"];
      const stateFilters: FilterOption[] = BestandsaufnahmeModel.getters("stateFilter");

      // online case is handled in composable after server call occurs
      if (isOffline || stateFilters.find(filter => filter.id === HzbaStatusCode.OFFLINE)) {
        BestandsaufnahmeModel.commit((state) => {
          state.searchResultsCount = results.length;
          state.totalSearchResultsCount = results.length;
        });
      }
    });

    const showLoadMore = computed(() => {
      return (
        BestandsaufnahmeModel.getters("pageCount") &&
        BestandsaufnahmeModel.getters("page") <
        BestandsaufnahmeModel.getters("pageCount")
      );
    });

    const loadMore = () => {
      BestandsaufnahmeModel.dispatch("loadBestandsaufnahmens");
    };

    return {
      appConfig,
      alert,
      bas,
      user,
      getPhotoUrl,
      openPopover: popoverHelper,
      loaded,
      doRefresh: useData.doRefresh,
      searchTerm,
      supportedSortTerms,
      sortTerm,
      lastFetch,
      swapVerticalOutline,
      t,
      filterOptions,
      selectedFilters,
      dateFilter,
      showLoadMore,
      loadMore,
      bestandsaufnahmes,
      featureFlags,
      canCreateSurvey,
      organisationPrefix,
      displayCountLabel,
      totalCount,
      addOutline,
      exportGeoJson,
      stopSelectMode,
      isSelectModeEnabled,
      selectAllCards,
      deselectAllCards,
      countOfSelectedCards,
      selectModeState,
      SelectModeState,
      openCreateSurveyModal
    };
  }
};
</script>

<style scoped>
.ion-searchbar {
  --background: var(--white100) !important;
  --background-color: var(--white100) !important;
  --border-radius: 8px;
  --box-shadow: 1px 1px 1px rgb(206, 206, 206);
  padding: 0px !important;
}

.create-survey-button {
  border-radius: 50%;
  background-color: var(--primary);
  color: white;
}

.button-group {
  display: flex;
  justify-content: center;
  align-items: center;
}

.group-button {
  margin: 0;
}

/* Left button: round left corners and left border */
.group-button.first {
  --border-radius: 8px 0 0 8px;
}

/* Middle button: no extra borders */
.group-button.middle {
  --border-left: none;
  --border-radius: 0;
}

/* Right button: round right corners, no right border override */
.group-button.last {
  --border-left: none;
  --border-radius: 0 8px 8px 0;
  --border-width: 1px 1px 1px 1px;
}
</style>
